import { observable } from '../reactivity';
import type { VideoFrameSource, VideoFramesProvider } from '../video/VideoFramesProvider';
import { VideoReadyState } from '../video/VideoFramesProvider';

export type VideoFramesProviderResourceOptions = {
    /** Start decoding the video immediately. */
    autoLoad?: boolean;
    /** Start playing the video as soon as it can play. */
    autoPlay?: boolean;
};

/**
 * Adapts a {@link VideoFramesProvider} to the surface {@link MediaResource} expects from every
 * media backing — `source`, dimensions, `valid`, `load()`, `destroy()` — which is the same shape
 * {@link WebpResource} implements.
 *
 * Deliberately not a `PIXI.Resource`: no texture is ever bound to this object. Assets read
 * {@link source} during their render pass and draw it into their own pooled canvas texture, so the
 * only thing this class owns is the provider's load lifecycle and its current frame. That is also
 * why it never touches the ticker.
 */
export class VideoFramesProviderResource {
    @observable.ref accessor #provider: VideoFramesProvider;
    /**
     * The provider the lifecycle listeners are attached to. It stays the one the resource was
     * loaded with even when {@link provider} is swapped to another playback region, so the
     * listeners are always removed from the object they were added to.
     */
    #listenedProvider: VideoFramesProvider | null = null;
    #autoPlay: boolean;
    #destroyed = false;
    #load: Promise<this> | null = null;
    #resolve: ((resource: this) => void) | null = null;
    #reject: ((error: unknown) => void) | null = null;

    constructor(provider: VideoFramesProvider, options: VideoFramesProviderResourceOptions = {}) {
        const { autoLoad = true, autoPlay = true } = options;

        this.#provider = provider;
        this.#autoPlay = autoPlay;

        if (autoLoad) {
            void this.load();
        }
    }

    /**
     * The provider frames are read from. Playback regions swap it to switch which decoder feeds
     * the asset; the swapped-in provider is already loaded by {@link MediaAsset}, so there is no
     * load lifecycle to restart here — {@link load} stays bound to the provider it first ran on.
     */
    get provider(): VideoFramesProvider {
        return this.#provider;
    }

    set provider(provider: VideoFramesProvider) {
        this.#provider = provider;
    }

    /**
     * The pixels of the frame currently displayed. Named `source` to match the PIXI resources this
     * one stands alongside in {@link MediaResource}.
     */
    get source(): VideoFrameSource | null {
        return this.#provider.currentFrame;
    }

    get width(): number {
        return this.#provider.videoWidth;
    }

    get height(): number {
        return this.#provider.videoHeight;
    }

    /** Whether the active provider has decoded enough to report a frame size. */
    get valid(): boolean {
        return this.width > 0 && this.height > 0;
    }

    get destroyed(): boolean {
        return this.#destroyed;
    }

    /**
     * Not `async`: an async wrapper would resolve one microtask later than PIXI's own resources do,
     * which shifts dependent redraws into a later build pass (see {@link MediaResource.load}).
     */
    // eslint-disable-next-line @typescript-eslint/promise-function-async
    load(): Promise<this> {
        if (this.#load) {
            return this.#load;
        }

        const provider = this.#provider;
        this.#listenedProvider = provider;
        provider.addEventListener('play', this.#onPlayStart);

        const isAlreadyReady = provider.readyState > VideoReadyState.HAVE_CURRENT_DATA;
        if (isAlreadyReady) {
            this.#onCanPlay();
        } else {
            provider.addEventListener('canplay', this.#onCanPlay);
            provider.addEventListener('canplaythrough', this.#onCanPlay);
            provider.addEventListener('error', this.#onError);
        }

        this.#load = new Promise((resolve, reject) => {
            if (isAlreadyReady) {
                resolve(this);
                return;
            }

            this.#resolve = resolve;
            this.#reject = reject;
            provider.load();
        });

        return this.#load;
    }

    destroy(): void {
        if (this.#destroyed) {
            return;
        }
        this.#destroyed = true;
        this.#removeLifecycleListeners();
        this.#provider.destroy();
    }

    #removeLifecycleListeners() {
        const provider = this.#listenedProvider;
        if (!provider) {
            return;
        }
        provider.removeEventListener('play', this.#onPlayStart);
        provider.removeEventListener('canplay', this.#onCanPlay);
        provider.removeEventListener('canplaythrough', this.#onCanPlay);
        provider.removeEventListener('error', this.#onError);
        this.#listenedProvider = null;
    }

    #onError = (event: Event) => {
        this.#listenedProvider?.removeEventListener('error', this.#onError);
        if (this.#reject) {
            this.#reject(event);
            this.#reject = null;
            this.#resolve = null;
        }
    };

    #onPlayStart = () => {
        if (!this.valid) {
            this.#onCanPlay();
        }
    };

    #onCanPlay = () => {
        const provider = this.#listenedProvider;
        provider?.removeEventListener('canplay', this.#onCanPlay);
        provider?.removeEventListener('canplaythrough', this.#onCanPlay);

        if (this.#resolve) {
            this.#resolve(this);
            this.#resolve = null;
            this.#reject = null;
        }

        if (!this.#provider.paused) {
            this.#onPlayStart();
        } else if (this.#autoPlay) {
            void this.#provider.play();
        }
    };
}
